import { cn } from "@util/tailwind";

// Define the allowed tag types
export type WorkTagType =
    | "Top Tier"
    | "High Impact"
    | "Consistent Performer"
    | "Technical Expertise"
    | "Creative Excellence"
    | "Global Exposure"
    | "Emerging Talent"
    | "AI-Literate";

export type EducationTagType =
    | "Top Tier"
    | "Award Winning"
    | "Academic Excellence";

export type TagType = WorkTagType | EducationTagType;

// Define the tag context to determine which tags are allowed
export type TagContext = "work" | "education";

interface ExperienceTagProps {
    type: TagType;
    className?: string;
}

export default function ExperienceTag({ type, className }: ExperienceTagProps) {
    // Define tag configurations
    const tagConfig = {
        // Work and Education Tags
        "Top Tier": {
            label: "Top Tier",
            color:
                "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
            contexts: ["work", "education"] as TagContext[],
        },
        "Award Winning": {
            label: "Award Winning",
            color:
                "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
            contexts: ["work", "education"] as TagContext[],
        },

        // Education-only Tags
        "Academic Excellence": {
            label: "Academic Excellence",
            color:
                "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300",
            contexts: ["education"] as TagContext[],
        },

        // Work-only Tags
        "High Impact": {
            label: "High Impact",
            color:
                "bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
            contexts: ["work"] as TagContext[],
        },
        "Consistent Performer": {
            label: "Consistent Performer",
            color:
                "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
            contexts: ["work"] as TagContext[],
        },
        "Technical Expertise": {
            label: "Technical Expertise",
            color: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
            contexts: ["work"] as TagContext[],
        },
        "Creative Excellence": {
            label: "Creative Excellence",
            color: "bg-pink-50 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300",
            contexts: ["work"] as TagContext[],
        },
        "Global Exposure": {
            label: "Global Exposure",
            color: "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
            contexts: ["work"] as TagContext[],
        },
        "Emerging Talent": {
            label: "Emerging Talent",
            color:
                "bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
            contexts: ["work"] as TagContext[],
        },
        "AI-Literate": {
            label: "AI Literate",
            color:
                "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
            contexts: ["work"] as TagContext[],
        },
    };

    const config = tagConfig[type];

    return (
        <span
            className={cn(
                "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                config.color,
                className
            )}
        >
            {config.label}
        </span>
    );
}

// Helper function to check if a tag is valid for a specific context
export function isValidTagForContext(
    tag: TagType,
    context: TagContext
): boolean {
    const tagConfig = {
        // Work and Education Tags
        "Top Tier": {
            contexts: ["work", "education"] as TagContext[],
        },
        "Award Winning": {
            contexts: ["work", "education"] as TagContext[],
        },

        // Education-only Tags
        "Academic Excellence": {
            contexts: ["education"] as TagContext[],
        },

        // Work-only Tags
        "High Impact": {
            contexts: ["work"] as TagContext[],
        },
        "Consistent Performer": {
            contexts: ["work"] as TagContext[],
        },
        "Technical Expertise": {
            contexts: ["work"] as TagContext[],
        },
        "Creative Excellence": {
            contexts: ["work"] as TagContext[],
        },
        "Global Exposure": {
            contexts: ["work"] as TagContext[],
        },
        "Emerging Talent": {
            contexts: ["work"] as TagContext[],
        },
        "AI-Literate": {
            contexts: ["work"] as TagContext[],
        },
    };

    return tagConfig[tag]?.contexts.includes(context) || false;
}

// Helper function to filter tags based on context
export function filterTagsByContext(
    tags: TagType[],
    context: TagContext
): TagType[] {
    return tags.filter((tag) => isValidTagForContext(tag, context));
}
